import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft, Save, MapPin, Link as LinkIcon, Image as ImageIcon, Plus, X, Trash2, GripVertical, FileText, Upload, MousePointer,
  Mountain, Route, User, Calendar, Map, Star, Info, Home, Phone, Mail, Globe, Music, Camera, ShoppingBag, Utensils, Trees, Building, Bike, Fish, Sunset, Ban
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImageUploader } from '@/components/ImageUploader';
import { PoiManager } from '@/components/PoiManager';
import { SurveyStats } from '@/components/SurveyStats';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Páginas internas disponibles en el kiosco
const INTERNAL_PAGES = [
  // { value: '/map', label: 'Mapa del municipio' },
  { value: '/survey', label: 'Encuesta de Satisfacción' },
] as const;
const MAX_CAROUSEL_ITEMS = 15;
const sanitizeFileName = (name: string): string => {
  const ext = name.split('.').pop() || '';
  const baseName = name.substring(0, name.lastIndexOf('.')) || name;

  const sanitized = baseName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics (accents)
    .replace(/[^a-z0-9]/g, '-') // Replace non-alphanumeric with hyphen
    .replace(/-+/g, '-') // Remove multiple hyphens
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

  return `${sanitized}.${ext}`;
};

interface HeroData {
  name: string;
  image_url: string;
  click_url: string;
}

// Iconos disponibles para los botones de navegación
export const NAV_BUTTON_ICONS = [
  'Mountain', 'Route', 'User', 'Calendar', 'Map', 'Star', 'Info',
  'Home', 'Phone', 'Mail', 'Globe', 'Music', 'Camera', 'ShoppingBag',
  'Utensils', 'Trees', 'Building', 'Bike', 'Fish', 'Sunset',
] as const;
export type NavButtonIcon = typeof NAV_BUTTON_ICONS[number];

export const ICON_MAP: Record<string, any> = {
  Mountain, Route, User, Calendar, Map, Star, Info,
  Home, Phone, Mail, Globe, Music, Camera, ShoppingBag,
  Utensils, Trees, Building, Bike, Fish, Sunset,
};

export interface NavButton {
  id: string;
  label: string;
  icon: NavButtonIcon;
  action: 'none' | 'external_url' | 'internal_page';
  url?: string;
  page?: string;
  imageUrl?: string;
}

interface Municipality {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  heroes_data: HeroData[];
  nav_buttons?: NavButton[];
  newsletter_url: string;
  carpetania_url: string;
  enjoy_url: string;
  proxy_blocked_domains?: string[];
  carousel_random_order?: boolean;
}

interface MunicipalityFormProps {
  municipality: Municipality | null;
  onClose: () => void;
  isSuperAdmin: boolean;
}

interface HeroFormData {
  name: string;
  imageFile: File | null;
  imagePreview: string | null;
  action: 'none' | 'external_url' | 'internal_page';
  url: string;
  page: string;
}

interface FeatureCarouselImage {
  id: string;
  municipality_id: string;
  image_url: string;
  display_order: number;
  is_hidden: boolean;
}

const isVideoUrl = (url: string | null) => {
  if (!url) return false;
  if (url.startsWith('data:video/')) return true;
  const cleanUrl = url.split('?')[0].toLowerCase();
  return (
    cleanUrl.endsWith('.mp4') ||
    cleanUrl.endsWith('.webm') ||
    cleanUrl.endsWith('.ogg') ||
    cleanUrl.endsWith('.mov') ||
    url.includes('video')
  );
};

const SortableImageItem = ({ image, onDelete }: {
  image: FeatureCarouselImage;
  onDelete: (id: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-card rounded-lg border shadow-sm h-[88px]"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded bg-muted">
        {isVideoUrl(image.image_url) ? (
          <video
            src={image.image_url}
            className="w-full h-full object-cover animate-pulse"
            muted
            playsInline
          />
        ) : (
          <img
            src={image.image_url}
            alt="Imagen carrusel inferior"
            className="w-full h-full object-cover"
          />
        )}
      </div>
      <div className="flex-1">
        <span className="truncate text-sm text-muted-foreground">
          Orden: {image.display_order + 1}
        </span>
      </div>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        onClick={() => onDelete(image.id)}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

interface SortableHeroItemProps {
  hero: HeroFormData;
  index: number;
  heroId: string;
  onFieldChange: (index: number, field: keyof Pick<HeroFormData, 'name' | 'action' | 'url' | 'page'>, value: string) => void;
  onImageChange: (file: File | null, index: number) => void;
  onRemove: (index: number) => void;
  onImageRemove: (index: number) => void;
  canRemove: boolean;
  isSuperAdmin: boolean;
}

const SortableHeroItem = ({
  hero,
  index,
  heroId,
  onFieldChange,
  onImageChange,
  onRemove,
  onImageRemove,
  canRemove,
  isSuperAdmin
}: SortableHeroItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: heroId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <AccordionItem value={heroId}>
        <div className="flex items-center">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-2">
            <GripVertical className="w-5 h-5 text-muted-foreground" />
          </div>
          <AccordionTrigger className="flex-1">
            {hero.name || `Hero ${index + 1}`}
          </AccordionTrigger>
        </div>
        <AccordionContent>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Nombre del Municipio *</Label>
              <Input
                value={hero.name}
                onChange={(e) => onFieldChange(index, 'name', e.target.value)}
                placeholder="ej: Horcajo de la Sierra"
                disabled={!isSuperAdmin}
                className={!isSuperAdmin ? "bg-muted cursor-not-allowed" : ""}
                required
              />
            </div>
            <ImageUploader
              label="Imagen Hero"
              preview={hero.imagePreview}
              onImageChange={(file) => onImageChange(file, index)}
              onRemove={() => onImageRemove(index)}
              recommendedResolution="985×250"
              maxSizeMB={5}
            />
            <div className="space-y-2">
              <Label>Acción al pulsar</Label>
              <Select
                value={hero.action}
                onValueChange={(val) => onFieldChange(index, 'action', val)}
                disabled={!isSuperAdmin}
              >
                <SelectTrigger className={!isSuperAdmin ? 'bg-muted cursor-not-allowed' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin acción</SelectItem>
                  <SelectItem value="external_url">URL externa (abre en vista web)</SelectItem>
                  <SelectItem value="internal_page">Página interna</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {hero.action === 'external_url' && (
              <div className="space-y-2">
                <Label>URL externa *</Label>
                <Input
                  type="url"
                  value={hero.url}
                  onChange={(e) => onFieldChange(index, 'url', e.target.value)}
                  placeholder="https://..."
                  disabled={!isSuperAdmin}
                  className={!isSuperAdmin ? 'bg-muted cursor-not-allowed' : ''}
                />
              </div>
            )}
            {hero.action === 'internal_page' && (
              <div className="space-y-2">
                <Label>Página interna *</Label>
                <Select
                  value={hero.page || ''}
                  onValueChange={(val) => onFieldChange(index, 'page', val)}
                  disabled={!isSuperAdmin}
                >
                  <SelectTrigger className={!isSuperAdmin ? 'bg-muted cursor-not-allowed' : ''}>
                    <SelectValue placeholder="Seleccionar página..." />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERNAL_PAGES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {canRemove && isSuperAdmin && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => onRemove(index)}
              >
                <X className="h-4 w-4 mr-2" />
                Eliminar Hero
              </Button>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
};

export const MunicipalityForm = ({ municipality, onClose, isSuperAdmin }: MunicipalityFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: municipality?.name || '',
    latitude: municipality?.latitude?.toString() || '',
    longitude: municipality?.longitude?.toString() || '',
    newsletter_url: municipality?.newsletter_url || 'https://totems.sierranortemadrid.org/newsletter',
    carpetania_url: municipality?.carpetania_url || 'https://totems.sierranortemadrid.org/carpetania',
    enjoy_url: municipality?.enjoy_url || 'https://totems.sierranortemadrid.org/enjoy',
    primary_color: (municipality as any)?.primary_color || '#005041',
    background_color: (municipality as any)?.background_color || '#9EBEB3',
  });

  // Nav buttons state
  const [navButtons, setNavButtons] = useState<NavButton[]>(
    () => (municipality as any)?.nav_buttons ?? []
  );

  // Bottom buttons state
  const [bottomButtons, setBottomButtons] = useState<NavButton[]>(
    () => (municipality as any)?.bottom_buttons ?? []
  );

  const [blockedDomains, setBlockedDomains] = useState<string[]>(
    () => municipality?.proxy_blocked_domains ?? []
  );
  const [newBlockedDomain, setNewBlockedDomain] = useState('');

  const normalizeDomain = (value: string): string | null => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return null;
    try {
      const url = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
      return url.hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  };

  const addBlockedDomain = () => {
    const domain = normalizeDomain(newBlockedDomain);
    if (!domain) {
      toast({ title: 'Dominio no válido', description: 'Introduce un dominio como youtube.com', variant: 'destructive' });
      return;
    }
    setBlockedDomains(current => current.includes(domain) ? current : [...current, domain]);
    setNewBlockedDomain('');
  };

  // Initialize heroes from existing data or with one empty hero
  const parseHeroAction = (clickUrl: string): Pick<HeroFormData, 'action' | 'url' | 'page'> => {
    if (!clickUrl) return { action: 'none', url: '', page: '' };
    if (clickUrl.startsWith('/')) return { action: 'internal_page', url: '', page: clickUrl };
    if (clickUrl.startsWith('http')) return { action: 'external_url', url: clickUrl, page: '' };
    return { action: 'none', url: '', page: '' };
  };

  const [heroes, setHeroes] = useState<HeroFormData[]>(() => {
    if (municipality?.heroes_data && municipality.heroes_data.length > 0) {
      return municipality.heroes_data.map(hero => ({
        name: hero.name,
        imageFile: null,
        imagePreview: hero.image_url || null,
        ...parseHeroAction(hero.click_url),
      }));
    }
    return [];
  });

  // Generate stable IDs for heroes (for drag & drop)
  const [heroIds, setHeroIds] = useState<string[]>(() => {
    if (municipality?.heroes_data && municipality.heroes_data.length > 0) {
      return municipality.heroes_data.map((_, i) => `hero-${Date.now()}-${i}`);
    }
    return [];
  });

  // Feature Carousel State
  const [carouselImages, setCarouselImages] = useState<FeatureCarouselImage[]>([]);
  const [carouselLoading, setCarouselLoading] = useState(false);
  const [newCarouselFile, setNewCarouselFile] = useState<File | null>(null);
  const [newCarouselPreview, setNewCarouselPreview] = useState<string | null>(null);
  const [carouselRandomOrder, setCarouselRandomOrder] = useState(
    () => municipality?.carousel_random_order ?? false
  );

  // PDF Upload State
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load carousel images when municipality exists
  useEffect(() => {
    if (municipality?.id) {
      loadCarouselImages();
    }
  }, [municipality?.id]);

  const loadCarouselImages = async () => {
    if (!municipality?.id) return;
    setCarouselLoading(true);
    try {
      const data = await api.carouselImages.list(municipality.id);
      setCarouselImages(data || []);
    } catch (error) {
      console.error('Error loading carousel images:', error);
    } finally {
      setCarouselLoading(false);
    }
  };

  const handleHeroImageChange = (file: File | null, index: number) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const newHeroes = [...heroes];
      newHeroes[index].imageFile = file;
      newHeroes[index].imagePreview = reader.result as string;
      setHeroes(newHeroes);
    };
    reader.readAsDataURL(file);
  };

  const handleHeroFieldChange = (index: number, field: keyof Pick<HeroFormData, 'name' | 'action' | 'url' | 'page'>, value: string) => {
    setHeroes(prev => prev.map((h, i) => i === index ? { ...h, [field]: value } : h));
  };

  const addHero = () => {
    if (heroes.length < 3) {
      setHeroes([...heroes, {
        name: '',
        imageFile: null,
        imagePreview: null,
        action: 'external_url',
        url: 'https://totems.sierranortemadrid.org/?pueblo=',
        page: '',
      }]);
      setHeroIds([...heroIds, `hero-${Date.now()}-${heroes.length}`]);
    }
  };

  const removeHero = (index: number) => {
    setHeroes(prev => prev.filter((_, i) => i !== index));
    setHeroIds(prev => prev.filter((_, i) => i !== index));
  };

  const handleHeroDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = heroIds.findIndex((id) => id === active.id);
    const newIndex = heroIds.findIndex((id) => id === over.id);

    setHeroes(arrayMove(heroes, oldIndex, newIndex));
    setHeroIds(arrayMove(heroIds, oldIndex, newIndex));
  };

  const handleHeroImageRemove = (index: number) => {
    const newHeroes = [...heroes];
    newHeroes[index].imageFile = null;
    newHeroes[index].imagePreview = null;
    setHeroes(newHeroes);
  };

  // Carousel handlers
  const handleCarouselImageChange = (file: File | null) => {
    setNewCarouselFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewCarouselPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveCarouselPreview = () => {
    setNewCarouselFile(null);
    setNewCarouselPreview(null);
  };

  // PDF Upload handlers
  const handlePdfSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: "Error",
          description: "Solo se permiten archivos PDF",
          variant: "destructive"
        });
        return;
      }
      if (file.size > 50 * 1024 * 1024) { // 50MB max
        toast({
          title: "Error",
          description: "El PDF no puede superar los 50MB",
          variant: "destructive"
        });
        return;
      }
      setPdfFile(file);
    }
  };

  const handlePdfUpload = async () => {
    if (!pdfFile || !municipality?.id) return;

    setPdfUploading(true);
    try {
      const sanitizedName = sanitizeFileName(pdfFile.name);
      const fileName = `${municipality.id}/${Date.now()}_${sanitizedName}`;

      const { url: publicUrl } = await api.storage.upload(pdfFile, fileName, 'pdfs');

      // Update the enjoy_url field with the local PDF URL
      setFormData(prev => ({ ...prev, enjoy_url: publicUrl }));
      setCurrentPdfUrl(publicUrl);
      setPdfFile(null);

      toast({
        title: "PDF subido",
        description: "El PDF se ha subido correctamente. Guarda el municipio para aplicar los cambios."
      });
    } catch (error: any) {
      console.error('Error uploading PDF:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo subir el PDF",
        variant: "destructive"
      });
    } finally {
      setPdfUploading(false);
    }
  };

  const handleRemovePdf = () => {
    setPdfFile(null);
  };

  const handleCarouselUpload = async () => {
    if (!newCarouselFile || !municipality?.id) return;

    try {
      const sanitizedName = sanitizeFileName(newCarouselFile.name);
      const fileName = `feature-carousel/${municipality.id}/${Date.now()}_${sanitizedName}`;
      const { url: publicUrl } = await api.storage.upload(newCarouselFile, fileName, 'images');

      const nextOrder = carouselImages.length > 0
        ? Math.max(...carouselImages.map(img => img.display_order)) + 1
        : 0;

      await api.carouselImages.create(municipality.id, {
        image_url: publicUrl,
        display_order: nextOrder,
      });

      toast({
        title: "Archivo añadido",
        description: "El archivo (imagen/vídeo) se ha añadido al carrusel inferior"
      });

      handleRemoveCarouselPreview();
      loadCarouselImages();
    } catch (error: any) {
      console.error('Error uploading carousel media:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo subir el archivo",
        variant: "destructive"
      });
    }
  };

  const handleCarouselDelete = async (id: string) => {
    try {
      await api.carouselImages.delete(municipality!.id, id);
      toast({ title: 'Archivo eliminado', description: 'El archivo ha sido eliminado del carrusel' });
      loadCarouselImages();
    } catch (error) {
      console.error('Error deleting carousel media:', error);
      toast({ title: 'Error', description: 'No se pudo eliminar el archivo', variant: 'destructive' });
    }
  };

  const handleCarouselDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = carouselImages.findIndex((img) => img.id === active.id);
    const newIndex = carouselImages.findIndex((img) => img.id === over.id);

    const newImages = arrayMove(carouselImages, oldIndex, newIndex);
    setCarouselImages(newImages);

    try {
      const updates = newImages.map((img, index) => ({
        id: img.id,
        display_order: index
      }));

      for (const update of updates) {
        await api.carouselImages.update(municipality!.id, update.id, { display_order: update.display_order });
      }

      toast({
        title: "Orden actualizado",
        description: "El orden de las imágenes ha sido actualizado"
      });
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el orden",
        variant: "destructive"
      });
      loadCarouselImages();
    }
  };

  const handleButtonImageUpload = async (file: File, isBottom: boolean, buttonIndex: number) => {
    try {
      const sanitizedName = sanitizeFileName(file.name);
      const fileName = `button-logos/${Date.now()}_${sanitizedName}`;
      const { url } = await api.storage.upload(file, fileName, 'images');

      if (isBottom) {
        setBottomButtons(prev => prev.map((b, i) => i === buttonIndex ? { ...b, imageUrl: url } : b));
      } else {
        setNavButtons(prev => prev.map((b, i) => i === buttonIndex ? { ...b, imageUrl: url } : b));
      }
      toast({
        title: "Logo subido",
        description: "El logo para el botón se ha subido correctamente."
      });
    } catch (error: any) {
      console.error('Error uploading button logo:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo subir la imagen",
        variant: "destructive"
      });
    }
  };

  const handleRemoveButtonImage = (isBottom: boolean, buttonIndex: number) => {
    if (isBottom) {
      setBottomButtons(prev => prev.map((b, i) => i === buttonIndex ? { ...b, imageUrl: undefined } : b));
    } else {
      setNavButtons(prev => prev.map((b, i) => i === buttonIndex ? { ...b, imageUrl: undefined } : b));
    }
  };

  const uploadImage = async (file: File, municipalityName: string, heroName: string, type: 'hero', index?: number) => {
    const fileExt = file.name.split('.').pop();

    // Helper to create a clean slug from any string
    const toSlug = (str: string): string => {
      return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics (accents)
        .replace(/[^a-z0-9]/g, '-')       // Only alphanumerics
        .replace(/-+/g, '-')              // No multiple hyphens
        .replace(/^-|-$/g, '');           // No leading/trailing hyphens
    };

    let fileName = '';

    if (type === 'hero' && index !== undefined) {
      const municipalitySlug = toSlug(municipalityName);
      const heroSlug = toSlug(heroName);
      fileName = `${municipalitySlug}-hero-${index}-${heroSlug}.${fileExt}`;
    } else {
      fileName = `${toSlug(municipalityName)}-${type}.${fileExt}`;
    }

    const filePath = `${fileName}`;

    const { url } = await api.storage.upload(file, filePath, 'images');
    const timestamp = Date.now();
    return `${url}?v=${timestamp}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {

      for (const hero of heroes) {
        const heroActionValue = hero.action === 'external_url' ? hero.url : hero.action === 'internal_page' ? hero.page : 'none';
        if (!hero.name || (hero.action !== 'none' && !heroActionValue)) {
          toast({
            title: "Error",
            description: "Todos los heroes deben tener nombre. Si tienen acción, el destino es obligatorio.",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
      }

      // Upload hero images and build heroes_data
      const heroesData: HeroData[] = [];
      for (let i = 0; i < heroes.length; i++) {
        const hero = heroes[i];
        let imageUrl = municipality?.heroes_data?.[i]?.image_url || '';

        if (hero.imageFile) {
          imageUrl = await uploadImage(hero.imageFile, formData.name, hero.name, 'hero', i);
        }

        // For non-superadmins, preserve original name and click_url
        const resolvedClickUrl = hero.action === 'external_url' ? (hero.url || '') :
          hero.action === 'internal_page' ? (hero.page || '') : '';
        heroesData.push({
          name: isSuperAdmin ? hero.name : (municipality?.heroes_data?.[i]?.name || hero.name),
          image_url: imageUrl,
          click_url: isSuperAdmin ? resolvedClickUrl : (municipality?.heroes_data?.[i]?.click_url || resolvedClickUrl)
        });
      }

      // Build municipality data - non-superadmins only update heroes_data (images only)
      const municipalityData: Record<string, any> = {
        heroes_data: heroesData as any,
        nav_buttons: navButtons,
        bottom_buttons: bottomButtons,
        proxy_blocked_domains: blockedDomains,
        carousel_random_order: carouselRandomOrder,
      };

      // Only superadmins can update these fields
      if (isSuperAdmin) {
        municipalityData.name = formData.name;
        municipalityData.latitude = parseFloat(formData.latitude);
        municipalityData.longitude = parseFloat(formData.longitude);
        municipalityData.newsletter_url = formData.newsletter_url;
        municipalityData.carpetania_url = formData.carpetania_url;
        municipalityData.enjoy_url = formData.enjoy_url;
        municipalityData.primary_color = formData.primary_color;
        municipalityData.background_color = formData.background_color;
      }

      if (municipality) {
        await api.municipalities.update(municipality.id, municipalityData);
        toast({ title: 'Municipio actualizado', description: `"${municipality.name}" ha sido actualizado exitosamente` });
      } else {
        const insertData = {
          name: formData.name,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude),
          heroes_data: heroesData as any,
          nav_buttons: navButtons,
          bottom_buttons: bottomButtons,
          proxy_blocked_domains: blockedDomains,
          carousel_random_order: carouselRandomOrder,
          newsletter_url: formData.newsletter_url,
          carpetania_url: formData.carpetania_url,
          enjoy_url: formData.enjoy_url,
          primary_color: formData.primary_color,
          background_color: formData.background_color,
        };
        await api.municipalities.create(insertData);
        toast({ title: 'Municipio creado', description: `"${formData.name}" ha sido creado exitosamente` });
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving municipality:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el municipio",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background p-8">
      <div className="max-w-6xl mx-auto">
        <Button onClick={onClose} variant="ghost" className="mb-6 hover:bg-muted">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver al panel
        </Button>

        <Card className="border-2 shadow-xl">
          <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-secondary/5">
            <CardTitle className="text-3xl">
              {municipality ? 'Editar Municipio' : 'Nuevo Municipio'}
            </CardTitle>
            <CardDescription className="text-base">
              {municipality ? 'Actualiza la información y las imágenes del municipio' : 'Completa todos los campos para crear un nuevo municipio'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Botón guardar sticky en la parte superior */}
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 pb-2 border-b">
                <Button type="button" variant="outline" onClick={onClose} className="w-full sm:flex-1 h-10 text-sm" disabled={loading}>Cancelar</Button>
                <Button type="submit" disabled={loading} className="w-full sm:flex-1 h-10 text-sm bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow">
                  {loading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Guardando...</> : <><Save className="mr-2 h-4 w-4" />{municipality ? 'Guardar cambios' : 'Crear Municipio'}</>}
                </Button>
              </div>
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Información Básica</h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-base">Nombre del Municipio *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ej: Raiguer" className={`text-base h-11 ${!isSuperAdmin ? "bg-muted cursor-not-allowed" : ""}`} disabled={!isSuperAdmin} required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="latitude" className="text-base">Latitud *</Label>
                    <Input id="latitude" type="number" step="any" value={formData.latitude} onChange={(e) => setFormData({ ...formData, latitude: e.target.value })} placeholder="40.9083" className={`text-base h-11 ${!isSuperAdmin ? "bg-muted cursor-not-allowed" : ""}`} disabled={!isSuperAdmin} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="longitude" className="text-base">Longitud *</Label>
                    <Input id="longitude" type="number" step="any" value={formData.longitude} onChange={(e) => setFormData({ ...formData, longitude: e.target.value })} placeholder="-3.5167" className={`text-base h-11 ${!isSuperAdmin ? "bg-muted cursor-not-allowed" : ""}`} disabled={!isSuperAdmin} required />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="primary_color" className="text-base">Color Principal (Botones y barras)</Label>
                    <div className="flex gap-3 items-center">
                      <Input
                        id="primary_color"
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        disabled={!isSuperAdmin}
                        className={`h-11 w-16 p-1 cursor-pointer ${!isSuperAdmin ? "bg-muted cursor-not-allowed" : ""}`}
                      />
                      <Input
                        type="text"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        disabled={!isSuperAdmin}
                        placeholder="#005041"
                        className={`text-base h-11 uppercase font-mono max-w-[120px] ${!isSuperAdmin ? "bg-muted cursor-not-allowed" : ""}`}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="background_color" className="text-base">Color de Fondo (Pantalla principal)</Label>
                    <div className="flex gap-3 items-center">
                      <Input
                        id="background_color"
                        type="color"
                        value={formData.background_color}
                        onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                        disabled={!isSuperAdmin}
                        className={`h-11 w-16 p-1 cursor-pointer ${!isSuperAdmin ? "bg-muted cursor-not-allowed" : ""}`}
                      />
                      <Input
                        type="text"
                        value={formData.background_color}
                        onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                        disabled={!isSuperAdmin}
                        placeholder="#9EBEB3"
                        className={`text-base h-11 uppercase font-mono max-w-[120px] ${!isSuperAdmin ? "bg-muted cursor-not-allowed" : ""}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Separator />

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Ban className="h-5 w-5 text-primary" />
                    Dominios bloqueados en el proxy
                  </CardTitle>
                  <CardDescription>
                    Impide navegar a estos dominios y sus subdominios desde las webs externas.
                    Los reproductores y otros contenidos incrustados pueden seguir mostrándose.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      value={newBlockedDomain}
                      onChange={(event) => setNewBlockedDomain(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addBlockedDomain();
                        }
                      }}
                      placeholder="Ej: youtube.com"
                      aria-label="Dominio que se va a bloquear"
                    />
                    <Button type="button" variant="outline" onClick={addBlockedDomain}>
                      <Plus className="mr-2 h-4 w-4" />
                      Añadir dominio
                    </Button>
                  </div>

                  {blockedDomains.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {blockedDomains.map(domain => (
                        <div key={domain} className="flex items-center gap-2 rounded-full bg-destructive/10 px-3 py-1.5 text-sm text-destructive">
                          <span>{domain}</span>
                          <button
                            type="button"
                            onClick={() => setBlockedDomains(current => current.filter(item => item !== domain))}
                            className="rounded-full hover:bg-destructive/10"
                            aria-label={`Permitir de nuevo ${domain}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No hay dominios bloqueados para este municipio.</p>
                  )}
                </CardContent>
              </Card>

              <Separator />

              {/* Heroes Section */}
              <Card>
                <CardHeader>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <ImageIcon className="h-5 w-5 text-primary" />
                        Imágenes Hero (1-3)
                      </CardTitle>
                      <CardDescription>Gestiona las imágenes y enlaces principales del municipio</CardDescription>
                    </div>
                    {heroes.length < 3 && isSuperAdmin && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addHero}
                        className="w-full sm:w-auto hover:bg-primary hover:text-primary-foreground"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Añadir Hero
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {heroes.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg">
                      No hay imágenes hero configuradas. El kiosco no mostrará la sección superior.
                    </p>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleHeroDragEnd}
                    >
                      <SortableContext
                        items={heroIds}
                        strategy={verticalListSortingStrategy}
                      >
                        <Accordion type="single" collapsible className="w-full">
                          {heroes.map((hero, index) => (
                            <SortableHeroItem
                              key={heroIds[index]}
                              hero={hero}
                              index={index}
                              heroId={heroIds[index]}
                              onFieldChange={handleHeroFieldChange}
                              onImageChange={handleHeroImageChange}
                              onRemove={removeHero}
                              onImageRemove={handleHeroImageRemove}
                              canRemove={isSuperAdmin}
                              isSuperAdmin={isSuperAdmin}
                            />
                          ))}
                        </Accordion>
                      </SortableContext>
                    </DndContext>
                  )}
                </CardContent>
              </Card>

              <Separator />

              {/* Feature Carousel Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ImageIcon className="h-5 w-5 text-primary" />
                    Carrusel Inferior (Imágenes y Vídeos)
                  </CardTitle>
                  <CardDescription>
                    Gestiona las imágenes y vídeos del carrusel que aparece en la parte inferior del totem
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {municipality?.id ? (
                    <>
                      <div className="flex items-start gap-3 rounded-lg border p-4">
                        <Checkbox
                          id="carousel-random-order"
                          checked={carouselRandomOrder}
                          onCheckedChange={(checked) => setCarouselRandomOrder(checked === true)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="carousel-random-order" className="cursor-pointer">
                            Mostrar en orden aleatorio
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Mezcla el carrusel cada vez que se carga el tótem. Guarda los cambios para aplicar esta opción.
                          </p>
                        </div>
                      </div>

                      {/* Upload new media */}
                      {carouselImages.length < MAX_CAROUSEL_ITEMS ? (
                        <div>
                          <h4 className="text-sm font-medium mb-3">Añadir nueva imagen o vídeo</h4>
                          <ImageUploader
                            preview={newCarouselPreview}
                            onImageChange={handleCarouselImageChange}
                            onRemove={handleRemoveCarouselPreview}
                            label="Subir imagen o vídeo para carrusel inferior"
                            recommendedResolution="1920×1080 (vídeo en formato mp4/webm/ogg o imagen)"
                            maxSizeMB={200}
                            accept="image/*,video/*"
                          />
                          {newCarouselFile && (
                            <Button
                              type="button"
                              onClick={handleCarouselUpload}
                              className="mt-3 w-full"
                            >
                              Subir archivo
                            </Button>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                          Has alcanzado el máximo de {MAX_CAROUSEL_ITEMS} archivos en el carrusel.
                        </p>
                      )}

                      {/* Current media list */}
                      <div>
                        <h4 className="text-sm font-medium mb-3">
                          Archivos actuales ({carouselImages.length})
                        </h4>
                        {carouselLoading ? (
                          <p className="text-muted-foreground text-center py-4">Cargando archivos...</p>
                        ) : carouselImages.length === 0 ? (
                          <p className="text-muted-foreground text-center py-8 bg-muted/30 rounded-lg">
                            No hay archivos en el carrusel. Sube el primer archivo (imagen o vídeo) arriba.
                          </p>
                        ) : (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleCarouselDragEnd}
                          >
                            <SortableContext
                              items={carouselImages.map(img => img.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="space-y-2">
                                {carouselImages.map((image) => (
                                  <SortableImageItem
                                    key={image.id}
                                    image={image}
                                    onDelete={handleCarouselDelete}
                                  />
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 bg-muted/30 rounded-lg">
                      <ImageIcon className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                      <p className="text-muted-foreground">
                        Guarda el municipio primero para gestionar el carrusel de imágenes
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Separator />

              {/* Nav Buttons Section */}
              {isSuperAdmin && (
                <Card>
                  <CardHeader>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <MousePointer className="h-5 w-5 text-primary" />
                          Botones de Navegación
                        </CardTitle>
                        <CardDescription>Configura los botones que aparecen en el kiosko. Si no configuras ninguno, se muestran los predeterminados.</CardDescription>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setNavButtons(prev => [...prev, {
                          id: `btn-${Date.now()}`,
                          label: '',
                          icon: 'Mountain',
                          action: 'external_url',
                          url: '',
                        }])}
                        className="w-full sm:w-auto hover:bg-primary hover:text-primary-foreground"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Añadir botón
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {navButtons.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg">
                        Sin botones personalizados — se usarán los predeterminados del sistema.
                      </p>
                    )}
                    {navButtons.map((btn, idx) => (
                      <div key={btn.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Botón {idx + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setNavButtons(prev => prev.filter((_, i) => i !== idx))}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Etiqueta del botón *</Label>
                            <Input
                              value={btn.label}
                              onChange={e => setNavButtons(prev => prev.map((b, i) => i === idx ? { ...b, label: e.target.value } : b))}
                              placeholder="Ej: Disfruta la Sierra"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Icono</Label>
                            <Select
                              value={btn.icon}
                              onValueChange={val => setNavButtons(prev => prev.map((b, i) => i === idx ? { ...b, icon: val as NavButtonIcon } : b))}
                            >
                              <SelectTrigger className="w-full">
                                <div className="flex items-center gap-2">
                                  {(() => {
                                    const IconComponent = ICON_MAP[btn.icon];
                                    return IconComponent ? <IconComponent className="h-4 w-4 text-muted-foreground" /> : null;
                                  })()}
                                  <SelectValue />
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                {NAV_BUTTON_ICONS.map(icon => {
                                  const IconComponent = ICON_MAP[icon];
                                  return (
                                    <SelectItem key={icon} value={icon}>
                                      <div className="flex items-center gap-2">
                                        {IconComponent && <IconComponent className="h-4 w-4" />}
                                        <span>{icon}</span>
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2 mt-2">
                          <Label className="text-xs font-semibold">Imagen/Logo personalizado (Opcional - Reemplaza texto e icono)</Label>
                          <div className="flex items-center gap-4">
                            {btn.imageUrl ? (
                              <>
                                <div className="relative h-12 w-24 bg-muted border rounded overflow-hidden flex items-center justify-center p-1">
                                  <img src={btn.imageUrl} alt="Logo botón" className="h-full w-full object-contain" />
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRemoveButtonImage(false, idx)}
                                  className="text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Eliminar logo
                                </Button>
                              </>
                            ) : (
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleButtonImageUpload(file, false, idx);
                                  }
                                }}
                                className="text-xs cursor-pointer max-w-sm"
                              />
                            )}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Tipo de acción</Label>
                          <Select
                            value={btn.action}
                            onValueChange={val => setNavButtons(prev => prev.map((b, i) => i === idx ? { ...b, action: val as NavButton['action'], url: '', page: '' } : b))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="external_url">URL externa (abre en vista web)</SelectItem>
                              <SelectItem value="internal_page">Página interna</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {btn.action === 'external_url' && (
                          <div className="space-y-1">
                            <Label className="text-xs">URL *</Label>
                            <Input
                              type="url"
                              value={btn.url ?? ''}
                              onChange={e => setNavButtons(prev => prev.map((b, i) => i === idx ? { ...b, url: e.target.value } : b))}
                              placeholder="https://..."
                            />
                          </div>
                        )}
                        {btn.action === 'internal_page' && (
                          <div className="space-y-1">
                            <Label className="text-xs">Página interna *</Label>
                            <Select
                              value={btn.page ?? ''}
                              onValueChange={val => setNavButtons(prev => prev.map((b, i) => i === idx ? { ...b, page: val } : b))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar página..." />
                              </SelectTrigger>
                              <SelectContent>
                                {INTERNAL_PAGES.map(p => (
                                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              <Separator />

              {isSuperAdmin && (
                <>
                  <Separator />

                  {/* Bottom Buttons Section */}
                  <Card>
                    <CardHeader>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <MousePointer className="h-5 w-5 text-primary" />
                            Botones inferiores
                          </CardTitle>
                          <CardDescription>Los dos botones grandes que aparecen en la parte baja del kiosco. Si no configuras ninguno, se muestran los predeterminados.</CardDescription>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setBottomButtons(prev => [...prev, {
                            id: `bbtn-${Date.now()}`,
                            label: '',
                            icon: 'Globe',
                            action: 'none',
                            url: '',
                          }])}
                          className="w-full sm:w-auto hover:bg-primary hover:text-primary-foreground"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Añadir botón
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {bottomButtons.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg">
                          Sin botones personalizados — se mostrarán los predeterminados del sistema.
                        </p>
                      )}
                      {bottomButtons.map((btn, idx) => (
                        <div key={btn.id} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-muted-foreground">Botón inferior {idx + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setBottomButtons(prev => prev.filter((_, i) => i !== idx))}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Texto del botón *</Label>
                              <Input
                                value={btn.label}
                                onChange={e => setBottomButtons(prev => prev.map((b, i) => i === idx ? { ...b, label: e.target.value } : b))}
                                placeholder="Ej: Accede a la web del municipio"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Tipo de acción</Label>
                              <Select
                                value={btn.action}
                                onValueChange={val => setBottomButtons(prev => prev.map((b, i) => i === idx ? { ...b, action: val as NavButton['action'], url: '', page: '' } : b))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Sin acción</SelectItem>
                                  <SelectItem value="external_url">URL externa (abre en vista web)</SelectItem>
                                  <SelectItem value="internal_page">Página interna</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="space-y-2 mt-2">
                            <Label className="text-xs font-semibold">Imagen/Logo personalizado (Opcional - Reemplaza texto)</Label>
                            <div className="flex items-center gap-4">
                              {btn.imageUrl ? (
                                <>
                                  <div className="relative h-12 w-24 bg-muted border rounded overflow-hidden flex items-center justify-center p-1">
                                    <img src={btn.imageUrl} alt="Logo botón" className="h-full w-full object-contain" />
                                  </div>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRemoveButtonImage(true, idx)}
                                    className="text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Eliminar logo
                                  </Button>
                                </>
                              ) : (
                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleButtonImageUpload(file, true, idx);
                                    }
                                  }}
                                  className="text-xs cursor-pointer max-w-sm"
                                />
                              )}
                            </div>
                          </div>
                          {btn.action === 'external_url' && (
                            <div className="space-y-1">
                              <Label className="text-xs">URL *</Label>
                              <Input
                                type="url"
                                value={btn.url ?? ''}
                                onChange={e => setBottomButtons(prev => prev.map((b, i) => i === idx ? { ...b, url: e.target.value } : b))}
                                placeholder="https://..."
                              />
                            </div>
                          )}
                          {btn.action === 'internal_page' && (
                            <div className="space-y-1">
                              <Label className="text-xs">Página interna *</Label>
                              <Select
                                value={btn.page ?? ''}
                                onValueChange={val => setBottomButtons(prev => prev.map((b, i) => i === idx ? { ...b, page: val } : b))}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Seleccionar página..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {INTERNAL_PAGES.map(p => (
                                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </>
              )}

              {/* ── POI Manager ─────────────────────────────────────── */}
              {/* {municipality?.id && (
                <>
                  <Separator />
                  <PoiManager municipalityId={municipality.id} isSuperAdmin={isSuperAdmin} />
                </>
              )} */}

              {/* ── Survey Stats ────────────────────────────────────── */}
              {municipality?.id && (
                <>
                  <Separator />
                  <SurveyStats municipalityId={municipality.id} />
                </>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 pt-6">
                <Button type="button" variant="outline" onClick={onClose} className="w-full sm:flex-1 h-10 sm:h-12 text-sm sm:text-base" disabled={loading}>Cancelar</Button>
                <Button type="submit" disabled={loading} className="w-full sm:flex-1 h-10 sm:h-12 text-sm sm:text-base bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-lg">
                  {loading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Guardando...</> : <><Save className="mr-2 h-4 w-4" />{municipality ? 'Actualizar Municipio' : 'Crear Municipio'}</>}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
